import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { useDispatch } from "react-redux";
import {
    ExpandOutlined,
    FullscreenExitOutlined,
    FullscreenOutlined,
} from "@ant-design/icons";

import "../assets/libs/fontawesome-5.2.0/css/all.css";
import "../assets/styles/index.scss";

import { Canvas } from "../components/canvas";
import service from "../service";
import {
    setSpinning,
    setShowZoomWatermark
} from "../redux/designerPropertiesSlice";
import { useCanvasZoom } from "../hooks/useCanvasZoom";
import { useSeatsVisibility } from "../hooks/useSeatsVisibility";
import { applyAutoFit, useAutoFitOnContainerResize } from "../hooks/useAutoFitOnContainerResize";
import { useSeatHubConnection } from "../hooks/useSeatHubConnection";
import { getSvgObject } from "../utils/ObjectUtil";
import { Icons } from "../enums";

const MapDetail = () => {
    const dispatch = useDispatch();
    const canvasRef = useRef();
    const mapContainerRef = useRef();
    const [connection, setConnection] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [showExpanded, setShowExpanded] = useState(false);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const { canZoomIn, canZoomOut } = useCanvasZoom(canvasRef);


    useEffect(() => {
        dispatch(setSpinning(true));
        initScript();
        return () => {
            dispatch(setShowZoomWatermark(false));
        };
    }, []);

    const handleHubChangeStatus = useCallback((value) => {
        canvasRef.current.handler.detailHandler.setType("seat");
        canvasRef.current.handler.detailHandler.handleObjectLockUpdate(value);
    }, []);

    useSeatHubConnection(connection, handleHubChangeStatus);


    // Controlar ejecución de lógica de zoom máximo/mínimo solo una vez por ciclo
    // Mostrar labels, iconos y propagar listeners solo cuando no se puede hacer más zoom in
    useSeatsVisibility(canvasRef, canZoomIn, isMapLoaded, (seat) => {
        if (seat.status !== "Free" || !seat.available) return;// Si el asiento no está libre, disponible,no hacer nada

        // Mostrar/ocultar iconos y labels
        const seatCircle = seat.item(0);
        seatCircle.set({ fill: `${seat.statusColor.Free}40`, stroke: seat.statusColor.Free });

        // si el asiento es accesible, de compañia o tiene vista restringida, mostrar icono
        if (seat.accesible || seat.companion || seat.restrictedView) {
            if (seat.restrictedView)
                seat.insertAt(getSvgObject(Icons.restricted, (seatCircle.radius * 1.7), '#000000'), 2, true);
            else if (seat.accesible)
                seat.insertAt(getSvgObject(Icons.accesible, (seatCircle.radius * 1.7), '#000000'), 2, true);
            else if (seat.companion)
                seat.insertAt(getSvgObject(Icons.companion, (seatCircle.radius * 1.7), '#000000'), 2, true);
        } else {
            const seatText = seat.item(1);
            seatText.set({
                visible: true
            });
        }
    }, (seat) => {
        if (seat.status !== "Free" || !seat.available) return; // Si el asiento no está libre o disponible, no hacer nada

        // Restaurar propiedades customizadas
        // Ocultar labels y restaurar iconos 
        const seatCircle = seat.item(0);
        seatCircle.set({ fill: `${seat.statusColor.Free}80`, stroke: "transparent" });

        const seatText = seat.item(1);
        seatText.set({ visible: false });

        const seatIcon = seat.item(2);
        if (seatIcon) {
            seat.remove(seatIcon);
        }
    });

    useAutoFitOnContainerResize(mapContainerRef, canvasRef, isMapLoaded, {
        alignTop: true,
    });

    const handleExpand = () => {
        if (!expanded) {
            window.redseats.onExpand(true);
        } else {
            window.redseats.onMinimize(false);
        }
        setTimeout(() => {
            applyAutoFit(canvasRef);
        }, 500);
        setExpanded(!expanded);
    };

    const initScript = async () => {
        window.redseats.setSettigs(process.env.REACT_APP_ENVIROMENT, "Detail");
        if (window.redseats.publicKey) await init();
        else window.redseats.component.callback = init;
    };

    const init = async () => {
        const mapEvent = await service.mapEvent.buildDetail();
        if (mapEvent.key) {
            setShowExpanded(window.redseats.showFullScreenButton);
            const mapCanvas = JSON.parse(mapEvent.mapCanvas);
            await handlers.onLoad(mapCanvas);

            //signalr
            const newConnection = new HubConnectionBuilder()
                .withUrl(`${process.env.REACT_APP_HUB_URL}object`, {
                    transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
                    withCredentials: true,
                })
                .withAutomaticReconnect()
                .build();

            setConnection(newConnection);
        }
    };

    const handlers = {
        onLoad: async ({ Objects }) => {
            if (Objects) {
                canvasRef.current.handler.clear(true);
                const data = Objects.filter((obj) => {
                    return obj.id;
                });
                await canvasRef.current.handler.importJSON(data, "detail");
            }
            canvasRef.current.handler.interactionMode = "detail";
            canvasRef.current.handler.enablePanning = true;
            dispatch(setSpinning(false));
            setIsMapLoaded(true);
        },
    };

    const handleZoomToFit = () => {
        applyAutoFit(canvasRef);
    }

    return (
        <div className="map">
            <div className="map-canvas-container" ref={mapContainerRef}>
                <div className="map-header-toolbar">
                    {showExpanded && <Button onClick={handleExpand}>
                        {expanded ? <FullscreenOutlined /> : <FullscreenExitOutlined />}
                    </Button>}
                    <Button
                        onClick={() => {
                            canvasRef.current.handler.zoomHandler.zoomOut();
                        }}
                        disabled={!canZoomOut}
                    >
                        <span className="icon-zoom-out"></span>
                    </Button>
                    <Button
                        onClick={handleZoomToFit}
                    >
                        <ExpandOutlined />
                    </Button>
                    <Button
                        onClick={() => {
                            canvasRef.current.handler.zoomHandler.zoomIn();
                        }}
                        disabled={!canZoomIn}
                    >
                        <span className="icon-icon-zoom-in"></span>
                    </Button>
                </div>
                <div
                    className="canvas-wrapper"
                    onMouseEnter={() => dispatch(setShowZoomWatermark(true))}
                    onMouseLeave={() => dispatch(setShowZoomWatermark(false))}
                >
                    <Canvas
                        ref={canvasRef}
                        editable={false}
                        selection={false}
                        canvasOption={{ backgroundColor: "#FFFFFF" }}
                        maxZoom={1000}
                        minZoom={100}
                        onZoom={() => dispatch(setShowZoomWatermark(false))}
                        zoomEnabled={true}
                        gridOption={{ enabllled: false }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MapDetail;
